import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Layers, X, ArrowLeft } from 'lucide-react';
import './SimplisticBulkRates.css';
import { useSimplisticTheme } from './SimplisticThemeContext';

// Settings can be legacy HTML or the current { text } format. Keep formatting,
// but never execute settings HTML or preserve event handlers, URLs or styles.
export function bulkRateContent(content) {
  const text = typeof content === 'string' ? content : typeof content?.text === 'string' ? content.text : '';
  if (!text.trim()) return [];
  const document = new DOMParser().parseFromString(text, 'text/html');
  const permitted = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'h2', 'h3', 'h4', 'span', 'div']);
  const blocked = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'form', 'input', 'button']);
  const convert = (node, key) => {
    if (node.nodeType === 3) return node.textContent;
    if (node.nodeType !== 1 || blocked.has(node.tagName.toLowerCase())) return null;
    const children = [...node.childNodes].map((child, i) => convert(child, `${key}-${i}`));
    return permitted.has(node.tagName.toLowerCase())
      ? React.createElement(node.tagName.toLowerCase(), { key }, ...children)
      : <React.Fragment key={key}>{children}</React.Fragment>;
  };
  const safeNodes = [...document.body.childNodes].map((node, i) => convert(node, `rate-${i}`));
  // Empty editor markup should receive the same explicit empty state as {}.
  const copy = document.body.cloneNode(true);
  copy.querySelectorAll([...blocked].join(',')).forEach(node => node.remove());
  return copy.textContent.trim() ? safeNodes : [];
}

export default function SimplisticBulkRates({ content, onClose, themeOverride }) {
  const { theme } = useSimplisticTheme();
  const dialogRef = useRef(null);
  const rates = useMemo(() => bulkRateContent(content), [content]);
  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  const onKeyDown = event => {
    event.stopPropagation();
    if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    if (event.key !== 'Tab') return;
    const buttons = [...dialogRef.current.querySelectorAll('button')];
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return createPortal(<div className="simple-bulk-rates-overlay simple-themed-overlay" data-simple-theme={themeOverride || theme} onClick={event => { event.stopPropagation(); if (event.target === event.currentTarget) onClose(); }}>
    <section className="simple-bulk-rates-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="simple-bulk-rates-title" aria-describedby="simple-bulk-rates-description" tabIndex={-1} onKeyDown={onKeyDown}>
      <header className="simple-bulk-rates-header">
        <div className="simple-bulk-rates-icon" aria-hidden="true"><Layers size={23} strokeWidth={1.7} /></div>
        <button type="button" className="simple-bulk-rates-close" aria-label="Close bulk buying rates" onClick={onClose}><X size={19} /></button>
        <span className="simple-bulk-rates-eyebrow">SELL IN BULK</span>
        <h2 id="simple-bulk-rates-title">Bulk buying rates</h2>
        <p id="simple-bulk-rates-description">For cards below this store’s standard buylist threshold.</p>
      </header>
      <div className="simple-bulk-rates-body">
        {rates.length > 0 ? <div className="simple-bulk-rates-content">{rates}</div> : <div className="simple-bulk-rates-empty"><h3>No bulk rates published</h3><p>This store hasn’t listed its bulk buying rates here. Please confirm rates with the store before sending bulk cards.</p></div>}
        <p className="simple-bulk-rates-note">Check the store’s condition and sorting requirements before preparing your cards.</p>
      </div>
      <footer className="simple-bulk-rates-footer"><span>Your existing buylist stays unchanged.</span><button type="button" onClick={onClose}><ArrowLeft size={16} aria-hidden="true" />Back to cards</button></footer>
    </section>
  </div>, document.body);
}
